import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
} from '@mui/material';
import Constants from '../../../../assests/Constants/constants';
import { beautifyNumber } from "../../../../Utils/FormatNumber";
import {
  PanelTableContainer as Container,
  VatPanelHeadCell as HeadCell,
  PanelBodyRow as BodyRow,
  PanelNumberCell as NumberCell,
  PanelStatusBadge as Badge,
  softPanelTableSx,
} from './panelTableStyles';

/* ---------- 2.  Component ---------- */

const VatTable = ({
  direction = 'ltr',
  headerStyle,
  t,
  netVatAmount = 0,
  incomesVatDetailed = {},
  expensesVatDetailed = {},
  equipmentVatDetailed = {},
  purchaseOfEquipmentExpensesVatAmount = 0,
  expensesVatAmount = 0,
  incomesVatAmount = 0,
  reportTax = {},
  downloadVATReport,
  permissionsState,
  userTypeContext,
  handleShowVatModal,
  VatTooltip,
}) => {
  /* ---------- helpers ---------- */

  const tip = (obj) => ({
    vatObject: obj,
    beautifyNumber,
    label16: t('VAT16'),
    label17: t('VAT17'),
    label18: t('VAT18'),
  });

  /* ----------  derived values ---------- */

  // Computed
  const computedVat17 =
    (incomesVatDetailed?.vat17 || 0) -
    (expensesVatDetailed?.vat17 || 0) -
    (equipmentVatDetailed?.vat17 || 0);
  
  const computedVat18 =
    (incomesVatDetailed?.vat18 || 0) -
    (expensesVatDetailed?.vat18 || 0) -
    (equipmentVatDetailed?.vat18 || 0);

  // Reported (parsed once)
  const reportedVatTransactions = parseFloat(reportTax?.vatTransactions) || 0;
  const reportedOtherVatTaxes = parseFloat(reportTax?.otherVATTaxes) || 0;
  const reportedEquipmentVat = parseFloat(reportTax?.equipmentVAT) || 0;

  const reportedNetVat =
    reportedVatTransactions - reportedOtherVatTaxes - reportedEquipmentVat;

  // Differences
  const diffNetVat = netVatAmount - reportedNetVat;
  const diffEquipmentVat =
    purchaseOfEquipmentExpensesVatAmount - reportedEquipmentVat;
  const diffExpensesVat = expensesVatAmount - reportedOtherVatTaxes;
  const diffIncomesVat = incomesVatAmount - reportedVatTransactions;

  return (
    <Container
      dir={direction === 'rtl' ? 'ltr' : 'rtl'}
      component={Paper}
      sx={{ width: '100%', overflowX: 'visible' }}
    >
      <Table
        size="medium"
        sx={softPanelTableSx}
      >
        {/* ---------- header ---------- */}
        <TableHead>
          <TableRow>
            <HeadCell>{t('NetVAT')}</HeadCell>
            <HeadCell>{t('EquipmentVAT')}</HeadCell>
            <HeadCell>{t('VATExpenses')}</HeadCell>
            <HeadCell>{t('VATTransactions')}</HeadCell>
            <HeadCell align="center">{t('Type')}</HeadCell>
          </TableRow>
        </TableHead>

        {/* ---------- body ---------- */}
        <TableBody>
          {/* 1️⃣  Computed */}
          <BodyRow>
            {/* Net VAT */}
            <NumberCell>
              <Tooltip
                arrow
                placement="top"
                title={
                  <div style={{ lineHeight: 1.5 }}>
                    <strong>{t('VAT17')}:</strong>{' '}
                    {beautifyNumber(computedVat17, 2, true)}
                    <br />
                    <strong>{t('VAT18')}:</strong>{' '}
                    {beautifyNumber(computedVat18, 2, true)}
                  </div>
                }
              >
                <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                  {beautifyNumber(netVatAmount, 2, true)}
                </span>
              </Tooltip>
            </NumberCell>

            {/* Equipment VAT */}
            <NumberCell>
              <VatTooltip
                {...tip(equipmentVatDetailed)}
                displayValue={purchaseOfEquipmentExpensesVatAmount}
              />
            </NumberCell>

            {/* Expenses VAT */}
            <NumberCell>
              <VatTooltip
                {...tip(expensesVatDetailed)}
                displayValue={expensesVatAmount}
              />
            </NumberCell>

            {/* Incomes VAT */}
            <NumberCell>
              <VatTooltip
                {...tip(incomesVatDetailed)}
                displayValue={incomesVatAmount}
              />
            </NumberCell>

            <TableCell align="center">
              <Tooltip title={t('DownloadVATReport')}>
                <Badge
                  label={t('Computed')}
                  palette="computed"
                  onClick={downloadVATReport}
                  clickable
                />
              </Tooltip>
            </TableCell>
          </BodyRow>

          {/* 2️⃣  Reported */}
          <BodyRow>
            <NumberCell>
              <span style={{ fontWeight: 600, color: '#2c3e50' }}>
                {beautifyNumber(reportedNetVat, 2, true)}
              </span>
            </NumberCell>
            <NumberCell>
              {beautifyNumber(reportedEquipmentVat, 2, true)}
            </NumberCell>
            <NumberCell>
              {beautifyNumber(reportedOtherVatTaxes, 2, true)}
            </NumberCell>
            <NumberCell>
              {beautifyNumber(reportedVatTransactions, 2, true)}
            </NumberCell>

            <TableCell align="center">
              <Tooltip title={t('ReportVATReport')}>
                <span
                  onClick={() => {
                    if (
                      !permissionsState?.ViewSubAccountantClients &&
                      userTypeContext?.toString() !== Constants.UserType.Client
                    ) {
                      handleShowVatModal();
                    }
                  }}
                >
                  <Badge label={t('Reported')} palette="reported" clickable />
                </span>
              </Tooltip>
            </TableCell>
          </BodyRow>

          {/* 3️⃣  Difference */}
          <BodyRow>
            <NumberCell>
              <span style={{ 
                fontWeight: 600, 
                color: diffNetVat >= 0 ? '#2e7d32' : '#d32f2f' 
              }}>
                {beautifyNumber(diffNetVat, 2, true)}
              </span>
            </NumberCell>
            <NumberCell>
              <span style={{ 
                color: diffEquipmentVat >= 0 ? '#2e7d32' : '#d32f2f' 
              }}>
                {beautifyNumber(diffEquipmentVat, 2, true)}
              </span>
            </NumberCell>
            <NumberCell>
              <span style={{ 
                color: diffExpensesVat >= 0 ? '#2e7d32' : '#d32f2f' 
              }}>
                {beautifyNumber(diffExpensesVat, 2, true)}
              </span>
            </NumberCell>
            <NumberCell>
              <span style={{ 
                color: diffIncomesVat >= 0 ? '#2e7d32' : '#d32f2f' 
              }}>
                {beautifyNumber(diffIncomesVat, 2, true)}
              </span>
            </NumberCell>
            <TableCell align="center">
              <Badge label={t('Difference')} palette="difference" />
            </TableCell>
          </BodyRow>
        </TableBody>
      </Table>
    </Container>
  );
};

/* ---------- 3.  Props ---------- */

VatTable.propTypes = {
  direction: PropTypes.string,
  headerStyle: PropTypes.object,
  t: PropTypes.func.isRequired,
  beautifyNumber: PropTypes.func.isRequired,
  netVatAmount: PropTypes.number,
  incomesVatDetailed: PropTypes.object,
  expensesVatDetailed: PropTypes.object,
  equipmentVatDetailed: PropTypes.object,
  purchaseOfEquipmentExpensesVatAmount: PropTypes.number,
  expensesVatAmount: PropTypes.number,
  incomesVatAmount: PropTypes.number,
  reportTax: PropTypes.object,
  downloadVATReport: PropTypes.func,
  permissionsState: PropTypes.object,
  userTypeContext: PropTypes.any,
  handleShowVatModal: PropTypes.func,
  VatTooltip: PropTypes.elementType.isRequired,
};

export default VatTable;